import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { hasPermission } from '@/lib/permissions';
import PermissionRequiredBanner from '@/components/PermissionRequiredBanner';
import Icon from '@/components/Icon';
import {
  listReviewQueue,
  distinctVendors,
  categorize,
  categoryLabel,
  categoryTone,
  effectiveVendor,
  effectiveEmailType,
  type ReviewCategory,
  type ReviewQueueRow,
  type QueueFilters,
} from '@/lib/reviewQueue';

// ============================================================
// ReviewPage — Phase 3, Epic 3A.6
// ============================================================
// Manager+ review queue for low-confidence parses, no-match
// dispatches, parse failures, and type-tagged emails (credit
// memo / bill-only / other).
//
// Two-pane mental model:
//   1. List (this page) — sorted, filterable index of items
//      needing attention. Tap a row to drill into the detail
//      editor.
//   2. Detail (/review/:id) — stacked editor with raw email
//      collapsible; commits via match_and_dispatch_parsed_email
//      force=true.
//
// Empty states:
//   - Permission absent → PermissionRequiredBanner. Default
//     copy shows since the page no longer hardcodes a
//     "managers and owners" message — post-3D.3 the view
//     permission is any_member; the banner is reachable only
//     if a future 7D permission_grants override tightens the
//     gate.
//   - Queue empty (all rows clean) → friendly "All caught up"
//     state with an inbox icon.
//   - Filters yield zero rows but queue has items → "No items
//     match the filters" with a clear-filters affordance.
//
// Bottom nav placement: 5th slot (added in 3A.6). From ship
// through 3D.2 this was manager+ (staff saw 4 tabs); 3D.3
// (2026-05-15) flipped review_queue.view to any_member per the
// posture policy, so all roles now see 5 tabs. The conditional
// rendering is preserved for forward-compat with 7D
// permission_grants tightenings.
// ============================================================

const CATEGORIES: readonly (ReviewCategory | null)[] = [
  null,
  'quarantined',
  'needs_match',
  'needs_review',
  'failed',
  'type_tagged',
] as const;

function ChipLabel({
  category,
}: {
  category: ReviewCategory | null;
}){
  return <>{category === null ? 'All' : categoryLabel(category)}</>;
}

/**
 * Parsed-at → human-readable. Relative for recent (< 7d),
 * absolute date for older. Mirrors ReorderPage's formatApprovalTime
 * to keep the visual register consistent.
 */
function formatRelativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const diffMin = diffMs / 60_000;
  const diffHour = diffMin / 60;
  const diffDay = diffHour / 24;
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`;
  if (diffHour < 24) return `${Math.floor(diffHour)}h ago`;
  if (diffDay < 7) return `${Math.floor(diffDay)}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function CategoryChip({
  category,
}: {
  category: ReviewCategory;
}){
  const tone = categoryTone(category);
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset ${tone.bg} ${tone.text} ${tone.ring}`}
    >
      {categoryLabel(category)}
    </span>
  );
}

export default function ReviewPage() {
  const session = useSession();
  const clinicId = session.activeMembership.clinic_id;
  const canView = hasPermission(session, 'inbound_email.review_queue.view');

  const [category, setCategory] = useState<ReviewCategory | null>(null);
  const [vendor, setVendor] = useState<string | null>(null);
  const [rows, setRows] = useState<ReviewQueueRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const filters = useMemo<QueueFilters>(
    () => ({ category, vendor }),
    [category, vendor],
  );

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoadError(null);
    setRows(null);
    listReviewQueue(clinicId, filters)
      .then((r) => {
        if (cancelled) return;
        setRows(r);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load queue');
      });
    return () => {
      cancelled = true;
    };
  }, [canView, clinicId, filters, reloadToken]);

  // Vendors list always derives from the UNFILTERED queue (without
  // vendor narrowing applied) so picking a vendor that yields zero
  // rows is still a valid action. We re-query when category changes
  // so the vendor dropdown narrows to vendors actually present in
  // the current category slice.
  const [vendorOptions, setVendorOptions] = useState<string[]>([]);
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    listReviewQueue(clinicId, { category, vendor: null })
      .then((r) => {
        if (cancelled) return;
        setVendorOptions(distinctVendors(r));
      })
      .catch(() => {
        if (cancelled) return;
        setVendorOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canView, clinicId, category, reloadToken]);

  // If the current vendor selection is no longer in options (e.g.
  // category changed and that vendor doesn't appear in the new
  // slice), clear it. Prevents stuck filters.
  useEffect(() => {
    if (vendor !== null && !vendorOptions.includes(vendor)) {
      setVendor(null);
    }
  }, [vendor, vendorOptions]);

  if (!canView) {
    return (
      <PermissionRequiredBanner
        permission="inbound_email.review_queue.view"
        message="The review queue is available to managers and owners. Ask your clinic admin if you need access."
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter chrome */}
      <div className="px-3 pt-3 pb-2 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1">
          {CATEGORIES.map((c) => {
            const isActive =
              c === null ? category === null : category === c;
            return (
              <button
                key={c ?? 'all'}
                type="button"
                onClick={() => setCategory(c)}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <ChipLabel category={c} />
              </button>
            );
          })}
        </div>

        {vendorOptions.length > 0 && (
          <div className="mt-2">
            <select
              value={vendor ?? ''}
              onChange={(e) => setVendor(e.target.value || null)}
              aria-label="Filter by vendor"
              className="w-full text-[12px] px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700"
            >
              <option value="">All vendors</option>
              {vendorOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* List body */}
      <div className="flex-1 overflow-y-auto">
        {loadError && (
          <div className="p-4 text-center text-sm text-red-700 bg-red-50 m-3 rounded-lg">
            <div className="font-medium">Couldn't load review queue</div>
            <div className="text-xs mt-1 font-mono break-words">
              {loadError}
            </div>
            <button
              type="button"
              onClick={reload}
              className="mt-2 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold"
            >
              Retry
            </button>
          </div>
        )}

        {!loadError && rows === null && (
          <div className="h-full flex items-center justify-center">
            <span
              className="w-7 h-7 border-[3px] border-slate-200 border-t-action rounded-full animate-spin"
              role="status"
              aria-label="Loading review queue"
            />
          </div>
        )}

        {!loadError && rows !== null && rows.length === 0 && (
          <EmptyState
            kind={
              category === null && vendor === null
                ? 'all_clean'
                : 'filtered_empty'
            }
            onClearFilters={() => {
              setCategory(null);
              setVendor(null);
            }}
          />
        )}

        {!loadError && rows !== null && rows.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {rows.map((r) => (
              <ReviewRow key={r.id} row={r} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface ReviewRowProps {
  row: ReviewQueueRow;
}

function ReviewRow({ row }: ReviewRowProps){
  const category = categorize(row);
  const emailType = effectiveEmailType(row);
  return (
    <li>
      <Link
        to={`/review/${row.id}`}
        className="block px-3 py-2.5 hover:bg-slate-50 active:bg-slate-100 transition"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-semibold text-slate-900 truncate">
                {effectiveVendor(row)}
              </span>
              {category && <CategoryChip category={category} />}
            </div>
            <div className="text-[11.5px] text-slate-500 truncate mt-0.5">
              {row.subject ?? '(no subject)'}
            </div>
            <div className="text-[10.5px] text-slate-400 mt-0.5 flex items-center gap-1.5">
              <span>{formatRelativeTime(row.parsed_at)}</span>
              {emailType && (
                <>
                  <span>·</span>
                  <span className="font-mono">{emailType}</span>
                </>
              )}
            </div>
          </div>
          <Icon
            name="chevright"
            className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1"
            strokeWidth={2}
          />
        </div>
      </Link>
    </li>
  );
}

function EmptyState({
  kind,
  onClearFilters,
}: {
  kind: 'all_clean' | 'filtered_empty';
  onClearFilters: () => void;
}){
  if (kind === 'all_clean') {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-500">
        <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mb-3">
          <Icon name="check" className="w-6 h-6 text-emerald-600" strokeWidth={2.5} />
        </div>
        <h2 className="text-sm font-semibold text-slate-700">All caught up</h2>
        <p className="text-xs mt-1 max-w-[260px]">
          No inbound emails need review. New low-confidence parses or
          unmatched orders will appear here.
        </p>
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-500">
      <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
        <Icon name="search" className="w-6 h-6 text-slate-400" strokeWidth={2} />
      </div>
      <h2 className="text-sm font-semibold text-slate-700">No items match</h2>
      <p className="text-xs mt-1">Try adjusting the filters.</p>
      <button
        type="button"
        onClick={onClearFilters}
        className="mt-3 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700"
      >
        Clear filters
      </button>
    </div>
  );
}
